import React, { useState } from 'react';
import SubmitButton from '../../../components/Common/SubmitButton';
import { CountryAdminModel } from './services/Country.service';

interface CountryFormProps {
  setShowForm: (value: boolean) => void;
  countryDetails: Partial<CountryAdminModel> | null;
  onSave: (data: Partial<CountryAdminModel>) => Promise<void>;
}

const CountryForm: React.FC<CountryFormProps> = ({ setShowForm, countryDetails, onSave }) => {
  const isEdit = !!countryDetails?.id;
  const [loader, setLoader] = useState(false);

  const [form, setForm] = useState<Partial<CountryAdminModel>>({
    id: countryDetails?.id || undefined,
    name: countryDetails?.name || '',
    code: countryDetails?.code || '',
    displayOrder: countryDetails?.displayOrder ?? 1,
    isActive: countryDetails?.isActive ?? true,
    isDefault: countryDetails?.isDefault ?? false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setForm((prev) => ({ ...prev, [name]: value ? parseInt(value, 10) : 0 }));
    } else if (name === 'code') {
      setForm((prev) => ({ ...prev, code: value.toUpperCase().slice(0, 2) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoader(true);
    try {
      await onSave(form);
    } finally {
      setLoader(false);
    }
  };

  const isValid = form.name?.trim() && form.code?.length === 2;

  return (
    <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">
          {isEdit ? 'Edit' : 'Create'} Country
        </h2>
        <button
          onClick={() => setShowForm(false)}
          className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Country Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="e.g. Canada"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            required
          />
        </div>

        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
            ISO-2 Code *
          </label>
          <input
            type="text"
            id="code"
            name="code"
            value={form.code}
            onChange={handleChange}
            placeholder="e.g. CA"
            maxLength={2}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 uppercase"
            required
            disabled={isEdit}
          />
          {isEdit && (
            <p className="text-xs text-gray-400 mt-1">ISO code cannot be changed after creation.</p>
          )}
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="accent-blue-500"
            />
            Active
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            <input
              type="checkbox"
              name="isDefault"
              checked={form.isDefault}
              onChange={handleChange}
              className="accent-yellow-400"
            />
            Default country
          </label>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <SubmitButton loader={loader} disable={!isValid || loader} title={isEdit ? 'Update' : 'Create'} />
        </div>
      </form>
    </div>
  );
};

export default CountryForm;
